import React, {useMemo, useState} from 'react';
import {
    Autocomplete,
    Avatar,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Constants from '../../../utils/constants';

/**
 * ClientSelectorV2 — composant "dumb" d'affichage / sélection de client(s).
 *
 * Props :
 *  - clients: Client[] (au format renvoyé par /api/clients hydra:member)
 *  - exploitants: Exploitant[] (optionnel, pour admin)
 *  - isAdmin: boolean — affiche le sélecteur d'exploitant
 *  - clientSelected: id | id[] | null — sélection courante (id ou tableau d'ids si multiple)
 *  - onClientSelected: (value) => void — renvoie l'id ou le tableau d'ids
 *  - exploitantSelected, onExploitantSelected: idem pour l'exploitant
 *  - multiple: boolean (default false) — active la multi-sélection
 *  - label: string (optionnel) — label du champ client
 *  - loading: boolean (optionnel) — champ désactivé avec un indicateur inline tant que la liste se charge
 *  - disabled: boolean (optionnel)
 */
export default function ClientSelectorV2({
                                             clients,
                                             exploitants,
                                             isAdmin,
                                             onClientSelected,
                                             clientSelected,
                                             exploitantSelected,
                                             onExploitantSelected,
                                             multiple = false,
                                             label = "Sélectionner le client",
                                             loading = false,
                                             disabled = false,
                                             inline = false,
                                         }) {

    // inputValue contrôlé pour pouvoir masquer le libellé du client sélectionné en mode single
    const [inputValue, setInputValue] = useState('');

    // Tri alphabétique des clients par libellé (insensible à la casse / accents)
    const sortedClients = useMemo(() => {
        const list = Array.isArray(clients) ? [...clients] : [];
        return list.sort((a, b) =>
            (a?.libelle ?? '').localeCompare(b?.libelle ?? '', 'fr', {sensitivity: 'base'})
        );
    }, [clients]);

    // Résolution de la valeur (id -> objet client) pour l'Autocomplete
    // Comparaison tolérante au type (l'id peut venir en number depuis l'API
    // ou en string depuis le localStorage / l'URL).
    const value = useMemo(() => {
        if (multiple) {
            const ids = (Array.isArray(clientSelected) ? clientSelected : []).map((v) => String(v));
            return sortedClients.filter((c) => ids.includes(String(c.id)));
        }
        if (clientSelected === null || clientSelected === undefined || clientSelected === '') {
            return null;
        }
        return sortedClients.find((c) => String(c.id) === String(clientSelected)) ?? null;
    }, [sortedClients, clientSelected, multiple]);

    const handleChange = (_event, newValue) => {
        if (!onClientSelected) return;
        if (multiple) {
            onClientSelected((newValue ?? []).map((c) => c.id));
        } else {
            onClientSelected(newValue ? newValue.id : null);
        }
    };

    // Récupère l'URL du logo : on essaie d'abord celui du client, sinon celui de l'exploitant
    const getLogoUrl = (client) => {
        const contentUrl =
            client?.logo?.contentUrl ?? client?.exploitant?.logo?.contentUrl ?? null;
        if (!contentUrl) return null;
        return `${Constants.API_ASSETS_USER_LOGO}${contentUrl}`;
    };

    const renderClientRow = (client, {inOption = false} = {}) => {
        const logoUrl = getLogoUrl(client);
        const typos = Array.isArray(client?.typologieFlux) ? client.typologieFlux : [];
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    py: inOption ? 0.5 : 0,
                }}
            >
                <Avatar
                    src={logoUrl ?? undefined}
                    alt={client?.libelle ?? ''}
                    sx={{width: 32, height: 32, bgcolor: 'grey.200', fontSize: 14}}
                >
                    {(client?.libelle ?? '?').charAt(0).toUpperCase()}
                </Avatar>
                <Typography sx={{flexGrow: 0, fontWeight: 500, mr: 1}}>
                    {client?.libelle}
                </Typography>
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, flexGrow: 1}}>
                    {typos.map((type) => (
                        <Chip
                            key={type}
                            size="small"
                            label={type}
                            variant="outlined"
                        />
                    ))}
                </Box>
            </Box>
        );
    };

    const autocomplete = (
        <Autocomplete
            multiple={multiple}
            fullWidth
            disablePortal={false}
            disableCloseOnSelect={multiple}
            options={sortedClients}
            value={value}
            loading={loading}
            loadingText="Chargement des clients…"
            disabled={disabled || loading}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={(_e, newInput, reason) => {
                if (!multiple && reason === 'reset') {
                    setInputValue('');
                    return;
                }
                setInputValue(newInput);
            }}
            clearOnBlur={!multiple}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            getOptionLabel={(option) => option?.libelle ?? ''}
            filterOptions={(options, {inputValue}) => {
                const q = (inputValue ?? '').trim().toLowerCase();
                if (!q) return options;
                return options.filter((o) => {
                    const l = (o?.libelle ?? '').toLowerCase();
                    const e = (o?.email ?? '').toLowerCase();
                    const typos = (o?.typologieFlux ?? []).join(' ').toLowerCase();
                    return l.includes(q) || e.includes(q) || typos.includes(q);
                });
            }}
            renderOption={(props, option) => {
                const {key, ...rest} = props;
                return (
                    <li key={option.id} {...rest}>
                        {renderClientRow(option, {inOption: true})}
                    </li>
                );
            }}
            renderTags={(selected, getTagProps) =>
                selected.map((option, index) => {
                    const tagProps = getTagProps({index});
                    const {key: tagKey, ...tagRest} = tagProps;
                    return (
                        <Chip
                            {...tagRest}
                            key={option.id}
                            avatar={
                                getLogoUrl(option) ? (
                                    <Avatar src={getLogoUrl(option)} alt={option.libelle}/>
                                ) : undefined
                            }
                            label={option.libelle}
                            size="small"
                        />
                    );
                })
            }
            renderInput={(params) => {
                const singleSelected = !multiple && value ? value : null;
                const startAdornment = singleSelected ? (
                    <Chip
                        avatar={
                            getLogoUrl(singleSelected) ? (
                                <Avatar src={getLogoUrl(singleSelected)} alt={singleSelected.libelle}/>
                            ) : undefined
                        }
                        label={singleSelected.libelle}
                        size="small"
                        onDelete={() => onClientSelected && onClientSelected(null)}
                        sx={{mr: 0.5}}
                    />
                ) : (params.InputProps && params.InputProps.startAdornment);
                return (
                    <TextField
                        {...params}
                        label={inline ? undefined : label}
                        placeholder={
                            loading
                                ? "Chargement des clients…"
                                : (multiple && Array.isArray(value) && value.length > 0) || singleSelected
                                    ? ''
                                    : (multiple ? "Rechercher / ajouter un client" : "Rechercher un client")
                        }
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment,
                            endAdornment: (
                                <>
                                    {loading && <CircularProgress size={18} aria-label="Chargement des clients"/>}
                                    {params.InputProps?.endAdornment}
                                </>
                            ),
                        }}
                    />
                );
            }}
        />
    );

    if (inline) {
        return autocomplete;
    }

    return (
        <React.Fragment>
            <Grid container size={12} alignItems={"center"} className={"mt-1"} spacing={3}>
                {isAdmin &&
                    <>
                        <Grid size={3} className={"mb-2"}>
                            Choix de l'exploitant :
                        </Grid>
                        <Grid size={9}>
                            <FormControl fullWidth>
                                <InputLabel id="label-exploitant-v2">Sélectionner l'exploitant :</InputLabel>
                                <Select
                                    labelId="label-exploitant-v2"
                                    label="Sélectionner l'exploitant :"
                                    variant="outlined"
                                    value={exploitantSelected ?? ""}
                                    onChange={(event) => {
                                        onExploitantSelected && onExploitantSelected(event.target.value);
                                    }}
                                >
                                    {exploitants && exploitants.map((e) => (
                                        <MenuItem key={e.id} value={e.id}>
                                            {e.libelle}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </>
                }

                <Grid size={3}>
                    Choix du client :
                </Grid>
                <Grid size={9}>
                    {autocomplete}
                </Grid>
            </Grid>
        </React.Fragment>
    );
}
